import {createContext, useState, useEffect, useCallback, useContext, useMemo, useRef, startTransition} from "react";
import {coerceNonNegativeNumber} from "../lib/utils";
import { TradingContext } from "./TradingContext";
import { AuthContext } from "./AuthContext";
import { cleanFloatingPointError } from "../utils/tickSizeUtils";
import { useGetTechnicalIndicators, useGetSpecificIndicator, useUpdateAppSettings, useUpdateAccountDefaults } from "../hooks/useBackend";
import useApi from '../hooks/useApi';
import { WatchlistService } from '../services/watchlistService';
import {
  readOptionsWorkspace,
  withExpandedExpiration,
  writeOptionsWorkspace,
} from '../utils/optionsWorkspaceState';
import { getRequiredChartCount } from '../utils/chartLayoutConfig';
import {
  applySavedLayoutToCharts,
  layoutHasMixedTickers,
  layoutHasMixedTimeframes,
  normalizeSavedChartLayouts,
  snapshotVisibleCharts,
} from '../utils/savedChartLayouts';
import {
  createWatchlistAction,
  renameWatchlistAction,
  deleteWatchlistAction,
  addTickerAction,
  removeTickerAction,
  reorderTickersAction,
} from '../state/watchlistsActions';
import { useTradeAlertsQuery, useWatchlistsQuery, useWatchlistTickersQuery } from '../hooks/useTradeStationQueries';
import { useQueryClient } from '@tanstack/react-query';
import { DEFAULT_WATCHLIST_NAME, DEFAULT_WATCHLIST_TICKERS, isPremadeWatchlistId, isVirtualWatchlistId, premadeWatchlistTickers } from '../constants/watchlistDefaults';
import { resolveTradableSymbol } from '../utils/futuresContracts';
import {
  configFromLegs,
  legsFromPricesAndConfig,
  pricesFromLegs,
  sortLegsByPrice,
  updateLegPrices,
} from '../utils/takeProfitLeg';
import {
  LAYOUT_STORAGE_KEYS,
  readLayoutBool,
  writeLayoutBool,
} from '../utils/layoutLocalStorage';
import { isManualBracketDraft } from '../utils/bracketDraftMode';
import { isBrokerLinked } from '../utils/brokerConnection';
import {
  clearOverlayAppSettings,
  ensureStoredOverlayFlags,
  patchOverlayAppSetting,
  readStoredOverlayFlag,
  resolveOverlayFlag,
  setOverlayAppSettings,
} from '../utils/overlayAppSettings';

export const useAppStateContext = () => {
  const DEFAULT_R_LEVEL = 2;
  const DEFAULT_PERCENTAGE_TP = 0.37;
  const DEFAULT_RISK = 300.0;
  const DEFAULT_RISK_PERCENTAGE = 2.0;
  
  // Get current selected account from TradingContext
  const { selectedAccount, positions, selectedTickerDetails, positionsLoading } = useContext(TradingContext);
  const currentAccountId = selectedAccount?.AccountID;
  
  // Get auth context for JWT token
  const { user, updateUserSettings } = useContext(AuthContext);
  const { getTechnicalIndicators } = useGetTechnicalIndicators();
  const { getSpecificIndicator } = useGetSpecificIndicator();
  
  // Server-backed settings: initialize with sane defaults (no localStorage)
  const { updateAppSettings } = useUpdateAppSettings();
  const { updateAccountDefaults } = useUpdateAccountDefaults();
  const { request } = useApi();
  const watchlistApi = useMemo(() => WatchlistService(request), [request]);
  const queryClient = useQueryClient();
  
  // Keep a ref to latest bracket templates to avoid stale closures in setters
  const bracketTemplatesRef = useRef([]);
  // Latest overlay values, so a second click does not persist a stale snapshot.
  const localValuesRef = useRef({});
  const overlayHydratedForUserRef = useRef(null);
  
  // Helper factories to reduce duplication. Each setter writes one field.
  const createAppSettingSetter = useCallback((key, setLocal) => {
    return async (valueOrUpdater) => {
      const previous = localValuesRef.current[key];
      const value = typeof valueOrUpdater === 'function' ? valueOrUpdater(previous) : valueOrUpdater;
      localValuesRef.current[key] = value;
      setLocal(value);
      patchOverlayAppSetting(key, value);
      if (!user?.token) return;
      return updateAppSettings(user.token, { [key]: value });
    };
  }, [user?.token, updateAppSettings]);

  const createAccountDefaultSetter = useCallback((field, setLocal) => {
    return async (value) => {
      const nextVal = coerceNonNegativeNumber(value);
      if (nextVal === null) return;
      setLocal(nextVal);
      if (!user?.token || !currentAccountId) return;
      const next = { ...(user.accountSettings || user.accountDefaults || {}) };
      next[currentAccountId] = { ...(next[currentAccountId] || {}), [field]: nextVal };
      // Update AuthContext BEFORE server call so subsequent operations use fresh data
      updateUserSettings({ accountSettings: next });
      await updateAccountDefaults(user.token, next);
    };
  }, [user?.token, currentAccountId, user?.accountDefaults, updateAccountDefaults, updateUserSettings]);

  // Initialize state (will hydrate from server settings via AuthContext consumer if available)
  const [riskPercentage, setRiskPercentage] = useState(DEFAULT_RISK_PERCENTAGE);
  const [risk, setRisk] = useState(DEFAULT_RISK);
  // Risk presets - user-defined quick-select risk amounts
  const DEFAULT_RISK_PRESETS = [25, 50, 100, 200, 250, 300, 400, 500, 1000];
  const [riskPresets, setRiskPresets] = useState(DEFAULT_RISK_PRESETS);
  const [trailingStopLoss, setTrailingStopLoss] = useState(true);
  const [stopLoss, setStopLossRaw] = useState(0);
  const [limitPrice, setLimitPriceRaw] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [manualQuantityOverride, setManualQuantityOverride] = useState(false);
  /** 'shares' | 'amount' | 'risk' — how the ticket sizes the order. Default risk $. */
  const [positionSizeMode, setPositionSizeMode] = useState('risk');
  /** Dollar amount to spend when positionSizeMode is 'amount'. */
  const [spendAmount, setSpendAmount] = useState(1000);
  /** When on, the ticket keeps a stop and a 1R take profit. */
  const [oneRSplit, setOneRSplit] = useState(false);
  const [limitSyncEnabled, setLimitSyncEnabledState] = useState(false);
  const [bracketRLevel, setBracketRLevel] = useState(DEFAULT_R_LEVEL);
  const [takeProfitPercentage, setTakeProfitPercentage] = useState(DEFAULT_PERCENTAGE_TP);
  const [entryTakeProfits, setEntryTakeProfitsRaw] = useState([]);
  const takeProfitPrices = useMemo(
    () => pricesFromLegs(entryTakeProfits),
    [entryTakeProfits],
  );
  const [takeProfitTouched, setTakeProfitTouched] = useState(false);
  const [stopLossTouched, setStopLossTouched] = useState(false);
  const [tpInitialized, setTpInitialized] = useState(false);
  /** null = entry-only/idle; 'template' = saved template; 'manual' = +TP/+SL or drag-built legs */
  const [bracketDraftMode, setBracketDraftMode] = useState(null);

  // Draft alert state - for creating alerts via bell icon
  const [draftAlert, setDraftAlert] = useState(null); // { ticker, price, alert_type: 'above' | 'below' }
  const [liquidityLevelsCount, setLiquidityLevelsCount] = useState(3);
  const [showStdDevLines, setShowStdDevLines] = useState(true);
  const [showOrders, setShowOrders] = useState(() => readStoredOverlayFlag('showOrders') ?? true);
  const [showTradeExecutions, setShowTradeExecutions] = useState(() => readStoredOverlayFlag('showTradeExecutions') ?? true);
  const [showRuleViolations, setShowRuleViolations] = useState(() => readStoredOverlayFlag('showRuleViolations') ?? false);
  const [showAlertsOnChart, setShowAlertsOnChart] = useState(() => readStoredOverlayFlag('showAlertsOnChart') ?? true);
  const [showCrosshair, setShowCrosshair] = useState(() => readStoredOverlayFlag('showCrosshair') ?? true);
  // Remembered chart toggles are loaded before any settings save in this commit.
  if (user) ensureStoredOverlayFlags();
  localValuesRef.current = {
    ...localValuesRef.current,
    showOrders,
    showTradeExecutions,
    showRuleViolations,
    showAlertsOnChart,
    showCrosshair,
  };
  const [lockOrderDragging, setLockOrderDragging] = useState(false);
  const [logScale, setLogScale] = useState(false);
  const [extendPriceLinesFull, setExtendPriceLinesFull] = useState(true); // Default to true (current behavior: full lines)
  // Chart UI customization (colors, candlestick fill)
  const [chartBackground, setChartBackgroundState] = useState(null); // null = use theme default
  const [candlestickUpColor, setCandlestickUpColorState] = useState(null);
  const [candlestickDownColor, setCandlestickDownColorState] = useState(null);
  const [candlestickFillUp, setCandlestickFillUpState] = useState(false); // Default hollow up candles
  const [candlestickFillDown, setCandlestickFillDownState] = useState(true); // Default filled down candles
  const [strategyEnabled, setStrategyEnabled] = useState(false);
  const [bracketType, setBracketType] = useState('tp_1');
  const [manualAtr, setManualAtr] = useState(2);
  const [timeInForce, setTimeInForce] = useState('DAY');
  const [orderType, setOrderType] = useState('');
  
  // Bracket Templates - custom user-defined bracket configurations
  // No "None" or "Stop Loss" template: + SL alone builds a stop-only bracket.
  const DEFAULT_BRACKET_TEMPLATES = [
    { id: 'tp_1', name: '1 Take Profit', atrMultiplier: 1.5, takeProfits: [{ percentage: 100, rMultiple: 1.0 }], isBuiltIn: true },
    { id: 'tp_2', name: '2 Take Profits', atrMultiplier: 1.5, takeProfits: [{ percentage: 50, rMultiple: 1.0 }, { percentage: 50, rMultiple: 2.0 }], isBuiltIn: true }
  ];
  const [bracketTemplates, setBracketTemplates] = useState(DEFAULT_BRACKET_TEMPLATES);
  const [selectedBracketTemplateId, setSelectedBracketTemplateId] = useState('tp_1');
  // Order-specific overrides (for current order only, not saved to template)
  const [orderBracketOverrides, setOrderBracketOverrides] = useState(null);
  
  // Keep ref updated with latest bracketTemplates
  useEffect(() => {
    bracketTemplatesRef.current = bracketTemplates;
  }, [bracketTemplates]);

  const getSelectedBracketTemplate = useCallback(() => {
    return bracketTemplates.find(t => t.id === selectedBracketTemplateId)
      || bracketTemplates[0]
      || DEFAULT_BRACKET_TEMPLATES[0];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracketTemplates, selectedBracketTemplateId]);

  // Unified bracket mode: 'r' | 'price' | 'percentage' | 'quantity' (legacy - kept for backwards compatibility)
  // - 'r': R-multiple prices, % allocation
  // - 'price': $ prices, % allocation  
  // - 'percentage': % allocation focus, R-multiple prices
  // - 'quantity': Direct qty allocation (no %), R-multiple prices
  const [bracketMode, setBracketMode] = useState('r');
  // Independent mode toggles for configure dropdown
  const [bracketAllocationMode, setBracketAllocationMode] = useState('percentage'); // 'percentage' or 'quantity'
  const [bracketPriceMode, setBracketPriceMode] = useState('r'); // 'r' (R-multiple), 'price' (absolute $), or 'percentage' (% from entry)
  
  // Trade Alerts state
  const [tradeAlerts, setTradeAlerts] = useState([]);
  const [tradeAlertsLoading, setTradeAlertsLoading] = useState(false);
  const [tradeAlertsError, setTradeAlertsError] = useState(null);
  
  // Technical Indicators state  
  const [indicators, setIndicators] = useState(null);
  const [indicatorsLoading, setIndicatorsLoading] = useState(false);
  const [indicatorsError, setIndicatorsError] = useState(null);
  const [previousBracketType, setPreviousBracketType] = useState('tp_1');
  
  // Track last bracket side preference (long/short) to preserve when switching charts
  const [lastBracketSide, setLastBracketSide] = useState('long');

  const storedWorkspace = useRef(readOptionsWorkspace());
  const [tradeWorkspaceView, setTradeWorkspaceViewState] = useState(storedWorkspace.current.view);
  const [optionsChainState, setOptionsChainStateState] = useState(storedWorkspace.current.chain);

  const setTradeWorkspaceView = useCallback((view) => {
    const next = view === 'options' ? 'options' : 'chart';
    setTradeWorkspaceViewState(next);
    setOptionsChainStateState((chain) => {
      writeOptionsWorkspace(next, chain);
      return chain;
    });
  }, []);

  const setOptionsChainState = useCallback((updater) => {
    setOptionsChainStateState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      writeOptionsWorkspace(tradeWorkspaceView, next);
      return next;
    });
  }, [tradeWorkspaceView]);

  const setExpirationExpanded = useCallback((underlying, date, open) => {
    setOptionsChainState((prev) => withExpandedExpiration(prev, underlying, date, open));
  }, [setOptionsChainState]);

  // Watchlists state
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState(null);
  const [watchlistTickers, setWatchlistTickers] = useState([]);
  const [watchlistsLoading, setWatchlistsLoading] = useState(false);
  const [watchlistsError, setWatchlistsError] = useState(null);
  const defaultWatchlistBootstrapRef = useRef(false);

  // Drop the previous account's selected watchlist before the new user's
  // lists arrive. Otherwise a leftover id (e.g. watchlist 8 from account A)
  // can fetch tickers with account B's token and 404.
  useEffect(() => {
    setSelectedWatchlistId(null);
    setWatchlistTickers([]);
    setWatchlists([]);
    defaultWatchlistBootstrapRef.current = false;
  }, [user?.id]);

  // Mobile UI collapse — localStorage only (per device; never appSettings)
  const [mobileWatchlistsCollapsed, setMobileWatchlistsCollapsed] = useState(() =>
    readLayoutBool(LAYOUT_STORAGE_KEYS.mobileWatchlistsCollapsed, false)
  );
  const [mobileChartCollapsed, setMobileChartCollapsed] = useState(() =>
    readLayoutBool(LAYOUT_STORAGE_KEYS.mobileChartCollapsed, false)
  );
  const [mobileOrderSettingsCollapsed, setMobileOrderSettingsCollapsed] = useState(() =>
    readLayoutBool(LAYOUT_STORAGE_KEYS.mobileOrderSettingsCollapsed, true)
  );
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    writeLayoutBool(LAYOUT_STORAGE_KEYS.mobileWatchlistsCollapsed, mobileWatchlistsCollapsed);
  }, [mobileWatchlistsCollapsed]);
  useEffect(() => {
    writeLayoutBool(LAYOUT_STORAGE_KEYS.mobileChartCollapsed, mobileChartCollapsed);
  }, [mobileChartCollapsed]);
  useEffect(() => {
    writeLayoutBool(LAYOUT_STORAGE_KEYS.mobileOrderSettingsCollapsed, mobileOrderSettingsCollapsed);
  }, [mobileOrderSettingsCollapsed]);

  // Global session template (applies to ALL charts)
  // Initialize as null to prevent stream from starting before settings load
  const [sessionTemplate, setSessionTemplateState] = useState(null);

  // Chart synchronization settings
  const [syncByTicker, setSyncByTickerState] = useState(false);
  const [syncByTimeframe, setSyncByTimeframeState] = useState(false);
  // Mirror the hovered crosshair onto other charts of the same ticker.
  const [syncCrosshair, setSyncCrosshairState] = useState(true);

  // Multi-chart state (NEW: supports multiple independent charts on desktop)
  // Initial state has no tickers - settings will load from backend and set SPY for active chart if needed
  // NOTE: sessionTemplate is NOT stored per-chart, it's a global setting above
  const [charts, setCharts] = useState([
    { id: 'chart1', ticker: null, interval: 5, unit: 'Minute', barsback: 200, indicators: [] },
    { id: 'chart2', ticker: null, interval: 15, unit: 'Minute', barsback: 200, indicators: [] }
  ]);
  const [activeChartId, setActiveChartId] = useState('chart1'); // The chart that is currently focused/clicked (shows lighter border)
  const [chartLayout, setChartLayout] = useState('single'); // 'single', 'side-by-side', 'stacked', 'quadrant', '3-column' - default to single
  const [savedChartLayouts, setSavedChartLayouts] = useState([]);
  const savedChartLayoutsRef = useRef([]);
  savedChartLayoutsRef.current = savedChartLayouts;
  const [targetingModeEnabled, setTargetingModeEnabled] = useState(() => {
    try {
      const cached = localStorage.getItem('targetingModeEnabled');
      return cached ? JSON.parse(cached) : false; // Default to disabled - users can place simple orders without targeting
    } catch (_) {
      return false;
    }
  });

  // Persist targeting mode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('targetingModeEnabled', JSON.stringify(targetingModeEnabled));
    } catch (_) {}
  }, [targetingModeEnabled]);

  // Position bracket mode for adding SL/TP brackets to EXISTING positions
  // Completely separate from targetingModeEnabled (which is for new entry brackets)
  const [positionBracketMode, setPositionBracketModeRaw] = useState({
    active: false,
    ticker: null,
    stopLoss: null,
    takeProfits: [],
  });

  const setPositionBracketMode = useCallback((updater) => {
    setPositionBracketModeRaw(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return {
        ...prev,
        ...next,
        // Ensure ticker is always set when active
        ticker: next.active !== undefined ? (next.ticker || prev.ticker) : prev.ticker
      };
    });
  }, []);

  // ticker must always be passed by the caller; defined here before activeChartTicker to avoid TDZ
  const startPositionBracketMode = useCallback((ticker, initialValues = {}) => {
    if (!ticker) {
      console.warn('[startPositionBracketMode] ticker is required');
      return;
    }
    const takeProfits = Array.isArray(initialValues.takeProfits) && initialValues.takeProfits.length > 0
      ? initialValues.takeProfits.map((leg) => ({ ...leg }))
      : legsFromPricesAndConfig(initialValues.takeProfitPrices || []);
    const slRaw = initialValues.stopLoss;
    const hasSl = slRaw != null && slRaw !== '' && Number.isFinite(Number(slRaw)) && Math.abs(Number(slRaw)) > 1e-12;
    const nTp = takeProfits.length;
    if (!hasSl && nTp > 0) {
      setSelectedBracketTemplateId(nTp >= 2 ? 'tp_2' : 'tp_1');
      setBracketType(nTp >= 2 ? 'tp_2' : 'tp_1');
    } else if (hasSl && nTp === 0) {
      setSelectedBracketTemplateId('stop_only');
      setBracketType('stop_only');
    } else if (hasSl && nTp > 0) {
      setSelectedBracketTemplateId(nTp >= 2 ? 'tp_2' : 'tp_1');
      setBracketType(nTp >= 2 ? 'tp_2' : 'tp_1');
    }
    // Position-exit drafting is separate from new-entry order type — clear so Buy/Sell
    // and entry limit lines do not stay mixed with Submit/Cancel.
    setOrderType('');
    setTargetingModeEnabled(false);
    setBracketDraftMode(null);
    setPositionBracketMode({
      active: true,
      ticker,
      stopLoss: initialValues.stopLoss || null,
      takeProfits,
    });
  }, [setPositionBracketMode, setSelectedBracketTemplateId, setBracketType, setOrderType, setTargetingModeEnabled]);

  const setPositionBracketSL = useCallback((price) => {
    setPositionBracketMode(prev => ({
      ...prev,
      stopLoss: price
    }));
  }, [setPositionBracketMode]);

  const setPositionBracketTakeProfits = useCallback((legs) => {
    const next = Array.isArray(legs) ? legs.map((leg) => ({ ...leg })) : [];
    setPositionBracketMode((prev) => ({
      ...prev,
      takeProfits: next,
    }));
  }, [setPositionBracketMode]);

  const setPositionBracketTPs = useCallback((prices) => {
    const nextPrices = Array.isArray(prices) ? prices : [];
    setPositionBracketMode((prev) => {
      const nextLegs = sortLegsByPrice(updateLegPrices(prev.takeProfits || [], nextPrices));
      return {
        ...prev,
        takeProfits: nextLegs,
      };
    });
  }, [setPositionBracketMode]);

  const addPositionBracketTP = useCallback((price) => {
    setPositionBracketMode((prev) => {
      const prevLegs = prev.takeProfits || [];
      const lastR = prevLegs.length > 0 ? prevLegs[prevLegs.length - 1].rMultiple : 0;
      const nextLegs = sortLegsByPrice([
        ...prevLegs,
        { price: Number(price), percentage: 0, rMultiple: lastR + 1.0 },
      ]);
      return {
        ...prev,
        takeProfits: nextLegs,
      };
    });
  }, [setPositionBracketMode]);

  const endPositionBracketMode = useCallback(() => {
    setPositionBracketMode({
      active: false,
      ticker: null,
      stopLoss: null,
      takeProfits: [],
    });
    // Clear draft-only overrides set while configuring position bracket TPs
    setOrderBracketOverrides(null);
  }, [setPositionBracketMode, setOrderBracketOverrides]);

  const positionBracketModeEnriched = useMemo(() => ({
    ...positionBracketMode,
    takeProfitPrices: pricesFromLegs(positionBracketMode.takeProfits || []),
  }), [positionBracketMode]);

  // When the user starts a new-entry targeting bracket, cancel any pending position bracket mode
  useEffect(() => {
    if (targetingModeEnabled && positionBracketMode?.active) {
      endPositionBracketMode();
    }
  }, [targetingModeEnabled, positionBracketMode?.active, endPositionBracketMode]);

  // Selecting a new-entry order type abandons position-exit drafting (restore Buy/Sell).
  const prevOrderTypeForPositionBracketRef = useRef(orderType);
  useEffect(() => {
    const prev = prevOrderTypeForPositionBracketRef.current;
    const next = orderType;
    prevOrderTypeForPositionBracketRef.current = next;
    if (!positionBracketMode?.active) return;
    if (next && next !== '' && next !== prev) {
      endPositionBracketMode();
    }
  }, [orderType, positionBracketMode?.active, endPositionBracketMode]);

  // Clearing the last unsubmitted position SL/TP should leave position-bracket mode entirely.
  useEffect(() => {
    if (!positionBracketMode?.active) return;
    const sl = positionBracketMode.stopLoss;
    const hasSl = sl != null && sl !== '' && Number.isFinite(Number(sl)) && Math.abs(Number(sl)) > 1e-12;
    const hasTp = Array.isArray(positionBracketMode.takeProfits) && positionBracketMode.takeProfits.length > 0;
    if (!hasSl && !hasTp) {
      endPositionBracketMode();
    }
  }, [
    positionBracketMode?.active,
    positionBracketMode?.stopLoss,
    positionBracketMode?.takeProfits,
    endPositionBracketMode,
  ]);

  // Track previous values to detect transitions
  const prevTargetingModeEnabled = useRef(targetingModeEnabled);
  const prevOrderType = useRef(orderType);
  // Preserve trade-ready order type across targeting on/off (mobile Buy/Sell stays available)
  const orderTypeBeforeTargetingRef = useRef(orderType || 'market');
  
  // Track when we're in the middle of switching charts to prevent useEffects from disabling targeting
  const isSwitchingChartsRef = useRef(false);

  // Auto-set order type to 'limit' when targeting mode is enabled and no order type is selected
  // Auto-disable targeting mode when order type is deselected while targeting is enabled
  useEffect(() => {
    // CRITICAL FIX: Don't disable targeting mode if we're in the middle of switching charts
    if (isSwitchingChartsRef.current) {
      return;
    }
    
    const targetingJustEnabled = !prevTargetingModeEnabled.current && targetingModeEnabled;
    const targetingJustDisabled = prevTargetingModeEnabled.current && !targetingModeEnabled;
    const orderTypeJustDeselected = prevOrderType.current && prevOrderType.current !== '' && (!orderType || orderType === '');
    
    // If targeting turns on with no order type (template picker), seed a limit entry.
    // Keep market / stop / limit as the user already chose them — +SL / +TP must not
    // convert a market order to limit.
    if (targetingJustEnabled) {
      if (orderType && orderType !== '') {
        orderTypeBeforeTargetingRef.current = orderType;
      }
      if (!orderType || orderType === '') {
        setOrderType('limit');
      }
    }
    // When targeting exits, restore prior trade-ready type — unless user cleared order entry
    else if (targetingJustDisabled) {
      if (!orderType || orderType === '') {
        // Stay idle (no price lines) after cancel / clear
      } else {
        const restore = orderTypeBeforeTargetingRef.current && orderTypeBeforeTargetingRef.current !== ''
          ? orderTypeBeforeTargetingRef.current
          : 'market';
        setOrderType(restore === 'limit' ? 'market' : restore);
      }
    }
    // Only disable targeting if orderType was JUST deselected (not if it was already empty)
    // AND targeting mode was already enabled (not just enabled)
    // This prevents disabling targeting when selecting a template with empty orderType
    else if (orderTypeJustDeselected && targetingModeEnabled && prevTargetingModeEnabled.current) {
      // Order type was deselected while targeting was already enabled - disable targeting
      // (brackets require an order type to function)
      setTargetingModeEnabled(false);
    }
    
    // Update refs for next render
    prevTargetingModeEnabled.current = targetingModeEnabled;
    prevOrderType.current = orderType;
  }, [targetingModeEnabled, orderType, setOrderType, setTargetingModeEnabled]);

  // selectedTicker: Derived from the ACTIVE chart's ticker
  // This is used for order entry (bracket lines, inputs, buy/sell buttons)
  // When targeting mode is disabled, still return the active chart's ticker so order entry can work
  // (targeting mode controls bracket display, not order entry availability)
  const selectedTicker = useMemo(() => {
    // Wait for settings to load before returning anything
    if (!settingsLoaded) {
      return null;
    }
    
    // If no charts exist at all, default to SPY as last resort
    if (!charts || charts.length === 0) {
      return 'SPY';
    }
    
    // Always use active chart's ticker (regardless of targeting mode)
    // This allows order entry to work even when targeting mode is disabled
    if (activeChartId) {
      const activeChart = charts.find(c => c.id === activeChartId);
      const ticker = activeChart?.ticker || 'SPY';
      return ticker;
    }
    
    // Fallback to first chart if no active chart
    return charts[0]?.ticker || 'SPY';
  }, [settingsLoaded, charts, activeChartId]);

  // Track previous stop loss for logging
  const prevStopLossRef = useRef(stopLoss);
  
  // Update ref when stopLoss changes externally
  useEffect(() => {
    prevStopLossRef.current = stopLoss;
  }, [stopLoss]);
  
  // Wrapped setters that automatically clean floating-point precision errors
  // Uses ticker details to round to proper decimal places based on tick size
  const setStopLoss = useCallback((value) => {
    prevStopLossRef.current = value;
    setStopLossRaw(cleanFloatingPointError(value, selectedTicker, selectedTickerDetails));
  }, [selectedTicker, selectedTickerDetails]);

  const setLimitPrice = useCallback((value) => {
    setLimitPriceRaw(cleanFloatingPointError(value, selectedTicker, selectedTickerDetails));
  }, [selectedTicker, selectedTickerDetails]);

  const setEntryTakeProfits = useCallback((legs) => {
    const next = Array.isArray(legs) ? legs.map((leg) => ({ ...leg })) : [];
    setEntryTakeProfitsRaw(next);
  }, []);

  const setTakeProfitPrices = useCallback((value) => {
    const nextPrices = Array.isArray(value) ? value : [];
    setEntryTakeProfitsRaw((prev) => updateLegPrices(prev, nextPrices));
  }, []);

  // activeChartTicker: Derived from the ACTIVE chart's ticker
  // This is used for position management and determines which position we're viewing
  const activeChartTicker = useMemo(() => {
    // Wait for settings to load before returning anything
    if (!settingsLoaded) {
      return null;
    }
    
    // If no charts exist at all, default to SPY as last resort
    if (!charts || charts.length === 0) {
      return 'SPY';
    }
    
    // Always use active chart's ticker (follows focus)
    if (activeChartId) {
      const activeChart = charts.find(c => c.id === activeChartId);
      const ticker = activeChart?.ticker || 'SPY';
      return ticker;
    }
    
    // Fallback to first chart if no active chart
    return charts[0]?.ticker || 'SPY';
  }, [settingsLoaded, charts, activeChartId]);

  // Derive chart settings from ACTIVE chart (for bracket calculations like ATR)
  const chartInterval = useMemo(() => {
    // Always use active chart
    if (activeChartId) {
      const chart = charts.find(c => c.id === activeChartId);
      return chart?.interval || 5;
    }
    // Fallback if no chart
    return charts[0]?.interval || 5;
  }, [charts, activeChartId]);

  const chartUnit = useMemo(() => {
    // Always use active chart
    if (activeChartId) {
      const chart = charts.find(c => c.id === activeChartId);
      return chart?.unit || 'Minute';
    }
    // Fallback if no chart
    return charts[0]?.unit || 'Minute';
  }, [charts, activeChartId]);

  // sessionTemplate is now a standalone global state (defined above with useState)

  // Track previous account ID to detect account changes
  const prevAccountIdRef = useRef(currentAccountId);

  // Keep the save-time overlay snapshot aligned with React state after hydration.
  useEffect(() => {
    if (!overlayHydratedForUserRef.current) return;
    setOverlayAppSettings({
      showOrders,
      showTradeExecutions,
      showRuleViolations,
      showAlertsOnChart,
      showCrosshair,
    });
  }, [showOrders, showTradeExecutions, showRuleViolations, showAlertsOnChart, showCrosshair]);

  // Hydrate from AuthContext user settings if available
  useEffect(() => {
    if (!user) {
      overlayHydratedForUserRef.current = null;
      clearOverlayAppSettings();
      setSettingsLoaded(false);
      return;
    }
    
    const app = user.appSettings || {};
    
    // === Multi-Chart Settings (NEW) ===
    const hasValidCharts = Array.isArray(app.charts) && app.charts.length > 0;
    
    if (hasValidCharts) {
      // Migration: Fix any charts with null tickers by using first valid ticker or fallback
      const firstValidTicker = app.charts.find(c => c.ticker)?.ticker || 'SPY';
      let needsMigration = false;
      const migratedCharts = app.charts.map(chart => {
        if (!chart.ticker) {
          needsMigration = true;
          return { ...chart, ticker: firstValidTicker };
        }
        const tradable = resolveTradableSymbol(chart.ticker);
        if (tradable && tradable !== String(chart.ticker).trim().toUpperCase()) {
          needsMigration = true;
          return { ...chart, ticker: tradable };
        }
        return chart;
      });
      
      setCharts(migratedCharts);
      
      // Set activeChartId if saved and valid, otherwise use first chart
      const savedActiveId = app.activeChartId || app.targetedChartId; // Support migration from old targetedChartId
      if (savedActiveId && migratedCharts.some(c => c.id === savedActiveId)) {
        setActiveChartId(savedActiveId);
      } else {
        setActiveChartId(migratedCharts[0].id);
      }
      
      // Persist migration to server if any charts were fixed
      if (needsMigration && user?.token) {
        updateAppSettings(user.token, { charts: migratedCharts }).catch(err => {
          console.error('Failed to persist migrated charts:', err);
        });
      }
    } else {
      // No saved charts (empty array, null, or undefined) - create defaults
      // This is the ONLY place we default to SPY
      const legacyChart1 = {
        id: 'chart1',
        ticker: 'SPY',
        interval: app.chartInterval || 5,
        unit: app.chartUnit || 'Minute',
        barsback: app.barsback || 200,
        indicators: []
      };
      setCharts([legacyChart1]);
      setActiveChartId('chart1');
    }
    
    // Chart layout
    setChartLayout(app.chartLayout || 'single');
    setSavedChartLayouts((prev) => {
      const next = normalizeSavedChartLayouts(app.savedChartLayouts);
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
    
    // === Global Settings ===
    // Session template (applies to ALL charts)
    setSessionTemplateState(app.sessionTemplate || 'Default');
    
    // Chart synchronization settings
    setSyncByTickerState(app.syncByTicker === true);
    setSyncByTimeframeState(app.syncByTimeframe === true);
    
    if (typeof app.liquidityLevelsCount === 'number') {
      const v = Math.max(1, Math.min(200, parseInt(app.liquidityLevelsCount, 10) || 3));
      setLiquidityLevelsCount(v);
    } else {
      setLiquidityLevelsCount(3);
    }
    setShowStdDevLines(app.showStdDevLines === true);
    // Overlay toggles hydrate once per signed-in user. `settingsLoaded` is read from the
    // effect closure and is not a reliable gate: a later user.appSettings update can still
    // see it as false and turn "show trade executions" back on (`!== false` treats a
    // missing key as enabled).
    const overlayUserKey = user.id ?? user.email ?? user.token ?? 'user';
    if (overlayHydratedForUserRef.current !== overlayUserKey) {
      overlayHydratedForUserRef.current = overlayUserKey;
      // Remembered browser value wins over the server blob, including when the
      // key is missing (missing showTradeExecutions is treated as on). The
      // choice is global — switching accounts must not turn it back on.
      const overlay = {
        showOrders: resolveOverlayFlag('showOrders', app.showOrders !== false),
        showTradeExecutions: resolveOverlayFlag('showTradeExecutions', app.showTradeExecutions !== false),
        showRuleViolations: resolveOverlayFlag('showRuleViolations', app.showRuleViolations === true),
        showAlertsOnChart: resolveOverlayFlag('showAlertsOnChart', app.showAlertsOnChart !== false),
        showCrosshair: resolveOverlayFlag('showCrosshair', app.showCrosshair !== false),
      };
      setShowOrders(overlay.showOrders);
      setShowTradeExecutions(overlay.showTradeExecutions);
      setShowRuleViolations(overlay.showRuleViolations);
      setShowAlertsOnChart(overlay.showAlertsOnChart);
      setShowCrosshair(overlay.showCrosshair);
      localValuesRef.current = { ...localValuesRef.current, ...overlay };
      setOverlayAppSettings(overlay);
    }
    if (!settingsLoaded) {
      setLockOrderDragging(app.lockOrderDragging === true);
      setLogScale(app.logScale === true);
      setSyncCrosshairState(app.syncCrosshair !== false);
      setExtendPriceLinesFull(app.extendPriceLinesFull !== false); // Default to true (full lines)
    }
    // Chart UI colors: only hydrate on initial load. Re-applying from user.appSettings on every
    // user update clobbers in-flight local changes when app.chartBackground is still undefined.
    if (!settingsLoaded) {
      setChartBackgroundState(app.chartBackground ?? null);
      setCandlestickUpColorState(app.candlestickUpColor ?? null);
      setCandlestickDownColorState(app.candlestickDownColor ?? null);
      setCandlestickFillUpState(app.candlestickFillUp === true);
      setCandlestickFillDownState(app.candlestickFillDown !== false);
    }
    setManualQuantityOverride(app.manualQuantityOverride === true);
    setLimitSyncEnabledState(app.limitSyncEnabled === true);
    // NOTE: takeProfitPrices are NOT loaded from settings - they should be calculated fresh based on current market conditions
    // Only restore bracketType from server on INITIAL load (same race-condition logic as orderType/selectedBracketTemplateId).
    // If we always restore from server, a stale 'none' written by stop_limit switching will keep clobbering
    // the local bracketType after the user has already switched back to a limit/bracket order.
    if (!settingsLoaded) {
      setBracketType(app.bracketType || 'tp_1');
    }
    setManualAtr(typeof app.manualAtr === 'number' ? app.manualAtr : 2);
    // Only restore timeInForce / orderType from server on INITIAL load.
    // Re-applying on every user.appSettings update races with in-flight TIF changes:
    // a slower save or refetch can still carry DAY and clobber a just-selected GTC,
    // so Buy/Sell submits DAY while the dropdown already shows GTC.
    if (!settingsLoaded) {
      setTimeInForce(app.timeInForce || 'DAY');
      setOrderType(
        isBrokerLinked(user) && app.orderType && String(app.orderType).trim()
          ? app.orderType
          : ''
      );
    }
    
    // Load bracket templates - merge user templates with built-in defaults
    // Filter out any built-in templates that user has deleted
    const deletedBuiltInIds = app.deletedBuiltInBracketIds || [];
    const filteredBuiltIns = DEFAULT_BRACKET_TEMPLATES.filter(t => !deletedBuiltInIds.includes(t.id));
    
    if (Array.isArray(app.bracketTemplates) && app.bracketTemplates.length > 0) {
      // Keep non-deleted built-in templates and add any user-created ones
      const userTemplates = app.bracketTemplates.filter(t => !t.isBuiltIn);
      const mergedTemplates = [...filteredBuiltIns, ...userTemplates];
      setBracketTemplates(mergedTemplates);
    } else {
      setBracketTemplates(filteredBuiltIns);
    }
    // Only apply selected bracket template from server on initial load (same race-condition fix as orderType)
    if (!settingsLoaded) {
      const selectedId = app.selectedBracketTemplateId || 'tp_1';
      const validSelectedId = deletedBuiltInIds.includes(selectedId) ? 'none' : selectedId;
      setSelectedBracketTemplateId(validSelectedId);
    }
    // Load bracket modes independently (with fallbacks)
    const loadedBracketMode = app.bracketMode || 'r';
    const loadedAllocationMode = app.bracketAllocationMode || 'percentage';
    const loadedPriceMode = app.bracketPriceMode || 'r';
    setBracketMode(loadedBracketMode);
    setBracketAllocationMode(loadedAllocationMode);
    setBracketPriceMode(loadedPriceMode);
    
    // === Watchlist Selection ===
    // Load saved watchlist selection if it exists (don't override with 'positions' default here)
    if (app.selectedWatchlistId !== undefined) {
      setSelectedWatchlistId(app.selectedWatchlistId);
    }
    
    // Note: chartInterval and chartUnit are derived from targeted chart
    // sessionTemplate is a global setting loaded above

    // Only apply risk presets on initial load to avoid race conditions
    // Risk values are loaded separately when account changes (see effect below)
    if (!settingsLoaded) {
      if (Array.isArray(app.riskPresets) && app.riskPresets.length > 0) {
        const mergedPresets = [...new Set([...DEFAULT_RISK_PRESETS, ...app.riskPresets])].sort((a, b) => a - b);
        setRiskPresets(mergedPresets);
      } else {
        setRiskPresets(DEFAULT_RISK_PRESETS);
      }
      // Load risk for initial account
      const defaults = user.accountSettings || user.accountDefaults || {};
      if (currentAccountId && defaults[currentAccountId]) {
        const acct = defaults[currentAccountId];
        if (typeof acct.risk === 'number') setRisk(acct.risk);
        if (typeof acct.riskPercentage === 'number') setRiskPercentage(acct.riskPercentage);
      }
    }
    
    // Mark settings as loaded after hydration
    setSettingsLoaded(true);
  }, [user, currentAccountId]);

  // Load risk when account changes (after initial load)
  useEffect(() => {
    if (!user || !settingsLoaded) return;
    
    const accountChanged = prevAccountIdRef.current !== currentAccountId;
    if (accountChanged && currentAccountId) {
      const defaults = user.accountSettings || user.accountDefaults || {};
      if (defaults[currentAccountId]) {
        const acct = defaults[currentAccountId];
        if (typeof acct.risk === 'number') {
          setRisk(acct.risk);
        }
        if (typeof acct.riskPercentage === 'number') {
          setRiskPercentage(acct.riskPercentage);
        }
      } else {
        // No saved risk for this account, use defaults
        setRisk(DEFAULT_RISK);
        setRiskPercentage(DEFAULT_RISK_PERCENTAGE);
      }
    }
    
    prevAccountIdRef.current = currentAccountId;
  }, [user, currentAccountId, settingsLoaded]);
  
  // Use deferred React Query for trade alerts
  const tradeAlertsQuery = useTradeAlertsQuery(!!user?.token);
  
  useEffect(() => {
    if (tradeAlertsQuery.data) {
      setTradeAlerts(tradeAlertsQuery.data);
    }
    setTradeAlertsLoading(tradeAlertsQuery.isLoading);
    setTradeAlertsError(tradeAlertsQuery.error?.message || null);
  }, [tradeAlertsQuery.data, tradeAlertsQuery.isLoading, tradeAlertsQuery.error]);
  
  // Legacy function for compatibility
  const fetchTradeAlerts = useCallback(async () => {
    await tradeAlertsQuery.refetch();
  }, [tradeAlertsQuery]);

  // Use deferred React Query for watchlists
  const watchlistsQuery = useWatchlistsQuery(!!user?.token);
  const isServerWatchlist = !!selectedWatchlistId && !isVirtualWatchlistId(selectedWatchlistId);
  const watchlistTickersQuery = useWatchlistTickersQuery(
    isServerWatchlist ? selectedWatchlistId : null,
    isServerWatchlist
  );

  const computePositionsTickers = useCallback(() => {
    const symbols = Array.from(new Set((positions || []).map(p => p.Symbol).filter(Boolean)));
    symbols.sort();
    return symbols.map((sym, idx) => ({ ticker: sym.toUpperCase(), position: idx + 1 }));
  }, [positions]);

  // Update watchlists from query
  useEffect(() => {
    if (watchlistsQuery.data) {
      // Inject virtual Positions watchlist at the top
      const withoutVirtual = watchlistsQuery.data.filter(w => w.id !== 'positions');
      setWatchlists([{ id: 'positions', name: 'Positions' }, ...withoutVirtual]);

      // Auto-select watchlist: prefer Positions when user has open positions and has not
      // saved a watchlist preference yet; otherwise first real watchlist.
      const savedWatchlistId = user?.appSettings?.selectedWatchlistId;
      const userChoseWatchlist =
        savedWatchlistId !== undefined && savedWatchlistId !== null;
      const hasOpenPositions = (positions || []).length > 0;

      setSelectedWatchlistId((prev) => {
        const currentIsValid =
          prev && (
            prev === 'positions'
            || isPremadeWatchlistId(prev)
            || withoutVirtual.some((w) => w.id === prev)
          );

        if (!userChoseWatchlist && hasOpenPositions) {
          return 'positions';
        }

        if (!currentIsValid && withoutVirtual.length > 0) {
          const firstId = withoutVirtual[0].id;
          if (user?.token) {
            updateAppSettings(user.token, { selectedWatchlistId: firstId }).catch(() => {});
          }
          return firstId;
        }

        return prev;
      });
    }
    setWatchlistsLoading(watchlistsQuery.isLoading);
    setWatchlistsError(watchlistsQuery.error?.message || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchlistsQuery.data,
    watchlistsQuery.isLoading,
    watchlistsQuery.error,
    user?.token,
    user?.appSettings?.selectedWatchlistId,
    positions,
  ]);

  useEffect(() => {
    if (!user?.token) {
      defaultWatchlistBootstrapRef.current = false;
    }
  }, [user?.token]);

  // Update watchlist tickers from query, positions, or premade popular lists
  useEffect(() => {
    if (selectedWatchlistId === 'positions') {
      setWatchlistTickers(computePositionsTickers());
    } else if (isPremadeWatchlistId(selectedWatchlistId)) {
      setWatchlistTickers(premadeWatchlistTickers(selectedWatchlistId));
    } else if (watchlistTickersQuery.data) {
      setWatchlistTickers(watchlistTickersQuery.data);
    }
  }, [selectedWatchlistId, watchlistTickersQuery.data, computePositionsTickers, positions]);

  // True when watchlist tickers are ready (positions watchlist uses positions; custom uses query)
  const watchlistTickersLoading = selectedWatchlistId === 'positions'
    ? positionsLoading
    : isPremadeWatchlistId(selectedWatchlistId)
      ? false
      : watchlistTickersQuery.isLoading;

  // Legacy functions for compatibility
  const loadWatchlists = useCallback(async () => {
    await watchlistsQuery.refetch();
  }, [watchlistsQuery]);

  const loadWatchlistTickers = useCallback(async (id) => {
    if (id === 'positions') {
      setWatchlistTickers(computePositionsTickers());
      return;
    }
    if (isPremadeWatchlistId(id)) {
      setWatchlistTickers(premadeWatchlistTickers(id));
      return;
    }
    await watchlistTickersQuery.refetch();
  }, [watchlistTickersQuery, computePositionsTickers]);

  // Server-backed watchlist setter (defined before action callbacks that use it)
  const setSelectedWatchlistIdServer = useMemo(() => createAppSettingSetter('selectedWatchlistId', setSelectedWatchlistId), [createAppSettingSetter]);

  const createWatchlist = useCallback(async (name) => {
    if (!user?.token) {
      throw new Error('Please log in to create watchlists.');
    }
    return createWatchlistAction(watchlistApi, name, loadWatchlists, setSelectedWatchlistIdServer);
  }, [watchlistApi, loadWatchlists, setSelectedWatchlistIdServer, user?.token]);

  // New accounts with zero watchlists: create "My Watchlist" once lists are loaded empty.
  useEffect(() => {
    if (!user?.token || !watchlistsQuery.data) return;
    const real = watchlistsQuery.data.filter((w) => w.id !== 'positions');
    if (real.length > 0) {
      defaultWatchlistBootstrapRef.current = false;
      return;
    }
    if (watchlistsQuery.isFetching) return;
    if (defaultWatchlistBootstrapRef.current) return;
    defaultWatchlistBootstrapRef.current = true;
    (async () => {
      try {
        await createWatchlistAction(watchlistApi, DEFAULT_WATCHLIST_NAME, loadWatchlists, setSelectedWatchlistIdServer, {
          initialTickers: DEFAULT_WATCHLIST_TICKERS,
          selectAfterCreate: false,
        });
      } catch {
        try {
          await loadWatchlists();
        } catch (_) {}
      }
    })();
  }, [
    user?.token,
    watchlistsQuery.data,
    watchlistsQuery.isFetching,
    watchlistApi,
    loadWatchlists,
    setSelectedWatchlistIdServer,
  ]);

  const renameWatchlist = useCallback(async (id, name) => {
    if (!user?.token) {
      throw new Error('Please log in to rename watchlists.');
    }
    return renameWatchlistAction(watchlistApi, id, name, loadWatchlists);
  }, [watchlistApi, loadWatchlists, user?.token]);

  const deleteWatchlist = useCallback(async (id) => {
    if (!user?.token) {
      throw new Error('Please log in to delete watchlists.');
    }
    return deleteWatchlistAction(watchlistApi, id, selectedWatchlistId, setSelectedWatchlistIdServer, setWatchlistTickers, loadWatchlists);
  }, [watchlistApi, loadWatchlists, selectedWatchlistId, setSelectedWatchlistIdServer, user?.token]);

  const addWatchlistTicker = useCallback(async (ticker) => {
    if (!selectedWatchlistId || isVirtualWatchlistId(selectedWatchlistId)) return;
    if (!user?.token) {
      throw new Error('Please log in to add tickers to your watchlist.');
    }
    return addTickerAction(watchlistApi, selectedWatchlistId, ticker, watchlistTickers, setWatchlistTickers, loadWatchlistTickers, queryClient);
  }, [watchlistApi, selectedWatchlistId, watchlistTickers, loadWatchlistTickers, queryClient, user?.token]);

  const removeWatchlistTicker = useCallback(async (ticker) => {
    if (!selectedWatchlistId || isVirtualWatchlistId(selectedWatchlistId)) return;
    if (!user?.token) {
      throw new Error('Please log in to remove tickers from your watchlist.');
    }
    const prev = watchlistTickers;
    return removeTickerAction(watchlistApi, selectedWatchlistId, ticker, prev, setWatchlistTickers, queryClient);
  }, [watchlistApi, selectedWatchlistId, watchlistTickers, queryClient, user?.token]);

  const reorderWatchlistTickers = useCallback(async (newOrder) => {
    if (!selectedWatchlistId || isVirtualWatchlistId(selectedWatchlistId)) return;
    if (!user?.token) {
      throw new Error('Please log in to reorder watchlist tickers.');
    }
    const prev = watchlistTickers;
    return reorderTickersAction(watchlistApi, selectedWatchlistId, newOrder, prev, setWatchlistTickers, queryClient);
  }, [watchlistApi, selectedWatchlistId, watchlistTickers, queryClient, user?.token]);
  
  // Fetch technical indicators
  const fetchIndicators = useCallback(async (ticker = selectedAccount?.ticker, timeframe = '1hour') => {
    if (!user?.token || !ticker) return;
    
    setIndicatorsLoading(true);
    setIndicatorsError(null);
    
    try {
      const response = await getTechnicalIndicators(user.token, ticker, timeframe);
      if (response && response.success) {
        setIndicators(response.indicators);
      } else {
        setIndicatorsError(response?.error || 'Failed to fetch indicators');
      }
    } catch (err) {
      setIndicatorsError(err.message);
    } finally {
      setIndicatorsLoading(false);
    }
  }, [user?.token, selectedAccount]);
  
  // Fetch specific indicator
  const fetchSpecificIndicator = useCallback(async (ticker, indicator, timeframe = '1hour', period = null) => {
    if (!user?.token || !ticker) return null;
    
    try {
      const response = await getSpecificIndicator(user.token, ticker, indicator, timeframe, period);
      if (response && response.success) {
        return response.value;
      }
    } catch (err) {
      console.error(`Error fetching ${indicator}:`, err);
    }
    return null;
  }, [user?.token, getSpecificIndicator]);

  // NOTE: Removed auto-disable targeting when bracket is 'none'
  // Users can enable targeting mode with any bracket, including 'none' for simple orders

  // NOTE: Removed auto-enable targeting when bracket type changes
  // Targeting mode should only be enabled when user explicitly selects a bracket template
  // This prevents unwanted targeting mode activation when changing order types
  useEffect(() => {
    // Just update previous bracket type for tracking
    setPreviousBracketType(bracketType);
  }, [bracketType]);

  // Auto-calc take profit legs on bracket type / template change (N × R-multiple).
  // Trade.js handles auto-reset on ticker change, so we don't interfere with that
  const prevBracketTypeRef = useRef(bracketType);
  useEffect(() => {
    const bracketTypeChanged = prevBracketTypeRef.current !== bracketType;
    if (!bracketTypeChanged) {
      return;
    }

    const selectedTemplate = getSelectedBracketTemplate?.();
    const templateTPs = (orderBracketOverrides && Array.isArray(orderBracketOverrides.takeProfits))
      ? orderBracketOverrides.takeProfits
      : (selectedTemplate?.takeProfits || []);
    const tpCount = templateTPs.length;
    const hasBracketTP = tpCount > 0;

    // +SL sets stop_only. Do not invent take-profit legs from the saved template,
    // and do not clear the manual lock that keeps template sync from adding them later.
    if (bracketType === 'stop_only' || isManualBracketDraft(bracketDraftMode)) {
      prevBracketTypeRef.current = bracketType;
      return;
    }

    if (!hasBracketTP) {
      // Panel "Add TP" may have already written draft legs while overrides/bracketType sync
      // is one frame behind — never wipe those legs on stop_only → tp_1 transitions.
      if (entryTakeProfits.length > 0) {
        prevBracketTypeRef.current = bracketType;
        return;
      }
      setEntryTakeProfits([]);
      setTpInitialized(false);
      setTakeProfitTouched(false);
      prevBracketTypeRef.current = bracketType;
      return;
    }

    // Manual draft: user is building legs — never overwrite with template on bracket type change.
    if (isManualBracketDraft(bracketDraftMode)) {
      prevBracketTypeRef.current = bracketType;
      return;
    }

    // Legacy guard: user dragged TPs in template mode — don't recalc until template changes.
    if (takeProfitTouched && bracketDraftMode === 'template') {
      prevBracketTypeRef.current = bracketType;
      return;
    }

    // + Bracket already priced every target; re-leveling here would move them.
    const pricedLegCount = entryTakeProfits.filter((leg) => Number(leg?.price) > 0).length;
    if (pricedLegCount === tpCount) {
      prevBracketTypeRef.current = bracketType;
      return;
    }

    const lp = parseFloat(limitPrice);
    const sl = parseFloat(stopLoss);
    if (!Number.isFinite(lp) || !Number.isFinite(sl) || lp <= 0 || sl <= 0) {
      // Don't mark template as processed — wait until entry/stop exist so TPs can seed
      return;
    }
    const stopDistance = Math.abs(lp - sl);
    if (stopDistance === 0) {
      return;
    }
    const isLong = sl < lp;
    const dir = isLong ? 1 : -1;
    const nextPrices = templateTPs.map((tp, idx) => {
      const rMult = tp?.rMultiple ?? (idx + 1);
      return lp + dir * (rMult * stopDistance);
    });
    setEntryTakeProfits(legsFromPricesAndConfig(nextPrices, templateTPs));
    setTpInitialized(true);
    setTakeProfitTouched(false);
    prevBracketTypeRef.current = bracketType;
  }, [
    bracketType,
    orderBracketOverrides,
    entryTakeProfits,
    bracketDraftMode,
    takeProfitTouched,
    limitPrice,
    stopLoss,
    getSelectedBracketTemplate,
  ]);

  // Initial load: if bracket has TP and we haven't initialized yet, compute once when prices load
  // NOTE: Trade.js handles auto-reset on ticker change, so this only runs on initial page load
  // This effect is DISABLED because it causes race conditions with manual reset buttons
  // Trade.js handles initialization properly when ticker changes
  // Manual resets (BracketActionsMenu, BracketRefreshButton) set TPs directly
  useEffect(() => {
    if (tpInitialized || takeProfitTouched) return;
    const selectedTemplate = getSelectedBracketTemplate?.();
    const templateTPs = selectedTemplate?.takeProfits || [];
    if (templateTPs.length === 0) return;
    const lp = parseFloat(limitPrice);
    const sl = parseFloat(stopLoss);
    if (!Number.isFinite(lp) || !Number.isFinite(sl) || lp <= 0 || sl <= 0) return;
    const stopDistance = Math.abs(lp - sl);
    if (stopDistance === 0) return;
    return;
    
    const isLong = sl < lp;
    const dir = isLong ? 1 : -1;
    const tp1 = lp + dir * stopDistance;
    const next = bracketType === 'tp_2' ? [tp1, lp + dir * (2 * stopDistance)] : [tp1];
    console.log('🎯 [AppStateContext] Auto-calc on initial load', { next, limitPrice, stopLoss, bracketType }, new Error().stack);
    setTakeProfitPrices(next);
    setTpInitialized(true);
    setTakeProfitTouched(false);
  }, [tpInitialized, takeProfitTouched, bracketType, limitPrice, stopLoss]);

  // NOTE: When ticker changes, Trade.js handles the auto-reset logic (including tpInitialized)
  // We don't need a separate useEffect here to avoid race conditions

  // Track bracket side (long/short) whenever valid bracket prices are set
  // This allows us to preserve the side preference when switching charts
  useEffect(() => {
    const lp = parseFloat(limitPrice);
    const sl = parseFloat(stopLoss);
    if (Number.isFinite(lp) && Number.isFinite(sl) && lp > 0 && sl > 0 && lp !== sl) {
      const isLong = sl < lp;
      const newSide = isLong ? 'long' : 'short';
      if (lastBracketSide !== newSide) {
        setLastBracketSide(newSide);
      }
    }
  }, [limitPrice, stopLoss, lastBracketSide]);

  const setAccountSpecificRiskPercentage = useMemo(() => createAccountDefaultSetter('riskPercentage', setRiskPercentage), [createAccountDefaultSetter]);
  const setAccountSpecificRisk = useMemo(() => createAccountDefaultSetter('risk', setRisk), [createAccountDefaultSetter]);
  
  // Debounce timer for risk changes
  const riskDebounceTimerRef = useRef(null);

  // Batched setter to update both risk and risk percentage together (prevents race conditions)
  const setRiskAndPercentage = useCallback(async (riskValue, riskPercentageValue) => {
    // Validate and coerce values
    const validRisk = coerceNonNegativeNumber(riskValue);
    const validRiskPct = coerceNonNegativeNumber(riskPercentageValue);
    if (validRisk === null || validRiskPct === null) return;
    
    // Update local state immediately for responsive UI
    setRisk(validRisk);
    setRiskPercentage(validRiskPct);
    
    // Clear existing timer
    if (riskDebounceTimerRef.current) {
      clearTimeout(riskDebounceTimerRef.current);
    }
    
    // Set new timer to save to backend after 1 second of no changes
    riskDebounceTimerRef.current = setTimeout(async () => {
      if (!user?.token || !currentAccountId) return;
      const next = { ...(user.accountDefaults || {}) };
      next[currentAccountId] = { 
        ...(next[currentAccountId] || {}), 
        risk: validRisk,
        riskPercentage: validRiskPct
      };
      // Update AuthContext BEFORE server call so subsequent operations use fresh data
      updateUserSettings({ accountDefaults: next });
      await updateAccountDefaults(user.token, next);
    }, 1000);
  }, [user?.token, currentAccountId, user?.accountDefaults, updateAccountDefaults, updateUserSettings]);

  // Risk presets setters with app settings persistence (global across all accounts)
  const setRiskPresetsServer = useCallback(async (presets) => {
    setRiskPresets(presets);
    if (!user?.token) return;
    await updateAppSettings(user.token, { riskPresets: presets });
  }, [user?.token, updateAppSettings]);

  const addRiskPreset = useCallback(async (amount) => {
    const numAmount = parseFloat(amount);
    if (!Number.isFinite(numAmount) || numAmount <= 0) return;
    const newPresets = [...riskPresets, numAmount].sort((a, b) => a - b);
    // Remove duplicates
    const uniquePresets = [...new Set(newPresets)];
    await setRiskPresetsServer(uniquePresets);
  }, [riskPresets, setRiskPresetsServer]);

  const removeRiskPreset = useCallback(async (amount) => {
    const newPresets = riskPresets.filter(p => p !== amount);
    await setRiskPresetsServer(newPresets);
  }, [riskPresets, setRiskPresetsServer]);

  // Local numeric setters with validation (block invalid/negative)
  const setStopLossValidated = useCallback((value) => {
    const nextVal = coerceNonNegativeNumber(value);
    if (nextVal === null) return;
    setStopLoss(nextVal);
  }, [setStopLoss]);

  const setLimitPriceValidated = useCallback((value) => {
    const nextVal = coerceNonNegativeNumber(value);
    if (nextVal === null) return;
    setLimitPrice(nextVal);
  }, [setLimitPrice]);

  const setQuantityValidated = useCallback((value) => {
    const nextVal = coerceNonNegativeNumber(value, { integer: true });
    if (nextVal === null || nextVal < 0) return;
    setQuantity(nextVal);
  }, []);

  const setManualQuantityOverrideServer = useMemo(() => createAppSettingSetter('manualQuantityOverride', setManualQuantityOverride), [createAppSettingSetter]);

  const setLimitSyncEnabled = useMemo(() => createAppSettingSetter('limitSyncEnabled', setLimitSyncEnabledState), [createAppSettingSetter]);

  // Helper function to get all account-specific risk settings (for debugging/management)
  const getAccountRiskSettings = useCallback(() => {
    if (!currentAccountId) return null;
    return {
      accountId: currentAccountId,
      risk,
      riskPercentage,
      source: 'server'
    };
  }, [currentAccountId, risk, riskPercentage]);

  // Wrap global setting setters to update server
  // showLiquidity is now derived from indicators, not persisted separately
  // const setShowLiquidityServer = useMemo(() => createAppSettingSetter('showLiquidity', setShowLiquidity), [createAppSettingSetter]); // REMOVED
  const setLiquidityLevelsCountServer = useMemo(() => createAppSettingSetter('liquidityLevelsCount', setLiquidityLevelsCount), [createAppSettingSetter]);
  const setShowStdDevLinesServer = useMemo(() => createAppSettingSetter('showStdDevLines', setShowStdDevLines), [createAppSettingSetter]);
  const setShowOrdersServer = useMemo(() => createAppSettingSetter('showOrders', setShowOrders), [createAppSettingSetter]);
  const setShowTradeExecutionsServer = useMemo(() => createAppSettingSetter('showTradeExecutions', setShowTradeExecutions), [createAppSettingSetter]);
  const setShowRuleViolationsServer = useMemo(() => createAppSettingSetter('showRuleViolations', setShowRuleViolations), [createAppSettingSetter]);
  const setShowAlertsOnChartServer = useMemo(() => createAppSettingSetter('showAlertsOnChart', setShowAlertsOnChart), [createAppSettingSetter]);
  const setShowCrosshairServer = useMemo(() => createAppSettingSetter('showCrosshair', setShowCrosshair), [createAppSettingSetter]);
  const setLockOrderDraggingServer = useMemo(() => createAppSettingSetter('lockOrderDragging', setLockOrderDragging), [createAppSettingSetter]);
  const setLogScaleServer = useMemo(() => createAppSettingSetter('logScale', setLogScale), [createAppSettingSetter]);
  const setExtendPriceLinesFullServer = useMemo(() => createAppSettingSetter('extendPriceLinesFull', setExtendPriceLinesFull), [createAppSettingSetter]);
  const setChartBackground = useMemo(() => createAppSettingSetter('chartBackground', setChartBackgroundState), [createAppSettingSetter]);
  const setCandlestickUpColor = useMemo(() => createAppSettingSetter('candlestickUpColor', setCandlestickUpColorState), [createAppSettingSetter]);
  const setCandlestickDownColor = useMemo(() => createAppSettingSetter('candlestickDownColor', setCandlestickDownColorState), [createAppSettingSetter]);
  const setCandlestickFillUp = useMemo(() => createAppSettingSetter('candlestickFillUp', setCandlestickFillUpState), [createAppSettingSetter]);
  const setCandlestickFillDown = useMemo(() => createAppSettingSetter('candlestickFillDown', setCandlestickFillDownState), [createAppSettingSetter]);
  const setBracketTypeServer = useMemo(() => createAppSettingSetter('bracketType', setBracketType), [createAppSettingSetter]);
  const setManualAtrServer = useMemo(() => createAppSettingSetter('manualAtr', setManualAtr), [createAppSettingSetter]);
  
  // Chart synchronization setters with server persistence
  const setSyncByTicker = useMemo(() => createAppSettingSetter('syncByTicker', setSyncByTickerState), [createAppSettingSetter]);
  const setSyncByTimeframe = useMemo(() => createAppSettingSetter('syncByTimeframe', setSyncByTimeframeState), [createAppSettingSetter]);
  const setSyncCrosshair = useMemo(() => createAppSettingSetter('syncCrosshair', setSyncCrosshairState), [createAppSettingSetter]);
  const setTimeInForceServer = useMemo(() => createAppSettingSetter('timeInForce', setTimeInForce), [createAppSettingSetter]);
  const setOrderTypeServer = useMemo(() => createAppSettingSetter('orderType', setOrderType), [createAppSettingSetter]);
  // Note: setSelectedWatchlistIdServer is defined before action callbacks to avoid dependency issues
  
  // Bracket Template setters with server persistence
  // Custom setter that preserves bracketTemplates to avoid race conditions
  const setSelectedBracketTemplateIdServer = useCallback(async (value) => {
    setSelectedBracketTemplateId(value);
    if (!user?.token) return;
    await updateAppSettings(user.token, { selectedBracketTemplateId: value });
  }, [user?.token, updateAppSettings]);
  
  // Unified bracket mode setter - updates bracketMode and derived legacy modes
  const setBracketModeServer = useCallback((mode) => {
    setBracketMode(mode);
    // Derive legacy modes from unified mode
    const newAllocationMode = mode === 'quantity' ? 'quantity' : 'percentage';
    const newPriceMode = mode === 'price' ? 'price' : 'r';
    setBracketAllocationMode(newAllocationMode);
    setBracketPriceMode(newPriceMode);
    // Persist to server
    if (user?.token) {
      updateAppSettings(user.token, {
        bracketMode: mode,
        bracketAllocationMode: newAllocationMode,
        bracketPriceMode: newPriceMode,
      });
    }
  }, [user?.token, updateAppSettings]);
  
  // Legacy setters (kept for backwards compatibility, but should use setBracketModeServer)
  const setBracketAllocationModeServer = useMemo(() => createAppSettingSetter('bracketAllocationMode', setBracketAllocationMode), [createAppSettingSetter]);
  const setBracketPriceModeServer = useMemo(() => createAppSettingSetter('bracketPriceMode', setBracketPriceMode), [createAppSettingSetter]);
  
  // Create a new bracket template
  const createBracketTemplate = useCallback(async (templateData) => {
    const newTemplate = {
      id: `template_${Date.now()}`,
      name: templateData.name || 'New Template',
      atrMultiplier: templateData.atrMultiplier ?? 1.5,
      takeProfits: templateData.takeProfits || [],
      stopLosses: templateData.stopLosses || [],
      risk: templateData.risk ?? 50.0, // Default risk in dollars
      riskPercentage: templateData.riskPercentage ?? 2.0, // Default risk percentage
      riskMode: templateData.riskMode ?? 'dollar', // 'dollar' or 'percentage'
      isBuiltIn: false
    };
    
    const updatedTemplates = [...bracketTemplates, newTemplate];
    setBracketTemplates(updatedTemplates);
    setSelectedBracketTemplateId(newTemplate.id); // Use local setter - persistence handled below
    
    // Persist to server (including BOTH bracketTemplates AND selectedBracketTemplateId)
    if (user?.token) {
      const userTemplates = updatedTemplates.filter(t => !t.isBuiltIn);
      await updateAppSettings(user.token, {
        bracketTemplates: userTemplates,
        selectedBracketTemplateId: newTemplate.id,
      });
    }
    
    return newTemplate;
  }, [bracketTemplates, user?.token, updateAppSettings]);
  
  // Update an existing bracket template
  const updateBracketTemplate = useCallback(async (templateId, updates) => {
    const template = bracketTemplates.find(t => t.id === templateId);
    if (!template || template.isBuiltIn) {
      console.warn('Cannot update built-in template');
      return;
    }
    
    const updatedTemplates = bracketTemplates.map(t => 
      t.id === templateId ? { ...t, ...updates } : t
    );
    setBracketTemplates(updatedTemplates);
    
    // Persist to server - Don't call updateUserSettings here to avoid race conditions
    if (user?.token) {
      const userTemplates = updatedTemplates.filter(t => !t.isBuiltIn);
      await updateAppSettings(user.token, { bracketTemplates: userTemplates });
    }
  }, [bracketTemplates, user?.token, updateAppSettings]);
  
  // Delete a bracket template (including built-in templates, except "None")
  const deleteBracketTemplate = useCallback(async (templateId) => {
    const template = bracketTemplates.find(t => t.id === templateId);
    if (!template || templateId === 'none') {
      console.warn('Cannot delete "None" template');
      return;
    }
    
    const updatedTemplates = bracketTemplates.filter(t => t.id !== templateId);
    setBracketTemplates(updatedTemplates);
    
    // If deleting selected template, switch to "none"
    const newSelectedId = selectedBracketTemplateId === templateId ? 'none' : selectedBracketTemplateId;
    if (selectedBracketTemplateId === templateId) {
      setSelectedBracketTemplateId('none');
    }
    
    // Persist to server - store custom templates AND list of deleted built-in IDs
    if (user?.token) {
      const userTemplates = updatedTemplates.filter(t => !t.isBuiltIn);
      await updateAppSettings(user.token, (latest) => ({
        bracketTemplates: userTemplates,
        deletedBuiltInBracketIds: template.isBuiltIn
          ? [...(latest.deletedBuiltInBracketIds || []), templateId]
          : (latest.deletedBuiltInBracketIds || []),
        selectedBracketTemplateId: newSelectedId,
      }));
    }
  }, [bracketTemplates, selectedBracketTemplateId, user?.token, updateAppSettings]);
  
  // Note: setSessionTemplate and chart interval/unit setters are defined after updateChartSettings below

  // === Multi-Chart Helper Functions ===
  
  // Get a specific chart by ID
  const getChart = useCallback((chartId) => {
    return charts.find(c => c.id === chartId);
  }, [charts]);

  // Get the active chart (the one showing bracket lines when targeting is enabled)
  const getTargetedChart = useCallback(() => {
    return charts.find(c => c.id === activeChartId) || charts[0];
  }, [charts, activeChartId]);

  /**
   * CENTRALIZED CHART SETTINGS UPDATE
   * Updates a chart's settings and handles bracket reset if updating active chart's ticker.
   * This is the single source of truth for all chart updates.
   */
  const updateChartSettings = useCallback(async (chartId, updates, options = {}) => {
    const { skipActivation = false } = options;
    if (updates?.ticker) {
      const tradable = resolveTradableSymbol(updates.ticker);
      if (tradable) updates = { ...updates, ticker: tradable };
    }
    
    // Get the chart before updating to check for ticker changes
    const chartBeforeUpdate = charts.find(c => c.id === chartId);
    const isActiveChart = chartId === activeChartId;
    const tickerIsChanging = updates.ticker && chartBeforeUpdate?.ticker !== updates.ticker;
    const timeframeIsChanging = (updates.interval !== undefined || updates.unit !== undefined) &&
      (chartBeforeUpdate?.interval !== (updates.interval ?? chartBeforeUpdate?.interval) ||
       chartBeforeUpdate?.unit !== (updates.unit ?? chartBeforeUpdate?.unit));

    // When sync is enabled and this is a user-initiated change (not from sync), apply to all charts
    // in one state update to avoid N API calls and N re-renders
    const shouldSyncInline = !skipActivation && (
      (syncByTicker && tickerIsChanging) ||
      (syncByTimeframe && timeframeIsChanging)
    );

    const updatedCharts = charts.map(chart => {
      if (chart.id === chartId) return { ...chart, ...updates };
      if (shouldSyncInline && syncByTicker && tickerIsChanging) return { ...chart, ticker: updates.ticker };
      if (shouldSyncInline && syncByTimeframe && timeframeIsChanging) {
        return { ...chart, interval: updates.interval ?? chart.interval, unit: updates.unit ?? chart.unit };
      }
      return chart;
    });
    setCharts(updatedCharts);
    
    // CRITICAL: If changing a ticker, make that chart active and reset bracket prices (if targeting is enabled)
    // This ensures bracket orders always match the chart the user is interacting with
    // BUT skip this during sync operations (skipActivation = true)
    if (tickerIsChanging && updates.ticker && !skipActivation) {
      if (!isActiveChart) {
        console.log('🎯 [updateChartSettings] Setting chart as active due to ticker change');
        setActiveChartId(chartId);
      }
      
      // Always reset limit/stop loss for new ticker (useful even when targeting disabled)
      setLimitPrice(0);
      setStopLoss(0);
      setStopLossTouched(false);
      
      // Only reset bracket-specific prices if targeting mode is enabled
      if (targetingModeEnabled) {
        setTakeProfitPrices([]);
        setTpInitialized(false);
        setTakeProfitTouched(false);
      }
    }

    // When sync-by-timeframe is enabled, make the chart being changed active so useChartSync
    // propagates the new timeframe to all other charts (instead of reverting to the old active chart's timeframe)
    if (timeframeIsChanging && syncByTimeframe && !skipActivation && !isActiveChart) {
      setActiveChartId(chartId);
    }

    // Persist to server in background
    if (user?.token) {
      const nextActiveId = (tickerIsChanging && updates.ticker && !skipActivation) || (timeframeIsChanging && syncByTimeframe && !skipActivation)
        ? chartId
        : activeChartId;
      await updateAppSettings(user.token, {
        charts: updatedCharts,
        activeChartId: nextActiveId,
        chartLayout,
        savedChartLayouts: savedChartLayoutsRef.current,
      });
    }
  }, [charts, activeChartId, targetingModeEnabled, chartLayout, syncByTicker, syncByTimeframe, user?.token, updateAppSettings, setLimitPrice, setStopLoss, setTakeProfitPrices, setTpInitialized]);

  /**
   * Batch update multiple charts in one state change + one API call.
   * Used by useChartSync to avoid N API requests when syncing ticker/timeframe across many charts.
   */
  const updateChartsInBatch = useCallback(async (updatesByChartId) => {
    const entries = Object.entries(updatesByChartId);
    if (entries.length === 0) return;

    const updatedCharts = charts.map(chart => {
      const updates = updatesByChartId[chart.id];
      if (!updates) return chart;
      return { ...chart, ...updates };
    });
    setCharts(updatedCharts);

    if (user?.token) {
      await updateAppSettings(user.token, {
        charts: updatedCharts,
        activeChartId,
        chartLayout,
        savedChartLayouts: savedChartLayoutsRef.current,
      });
    }
  }, [charts, activeChartId, chartLayout, user?.token, updateAppSettings]);

  // Chart settings setters - defined here after updateChartSettings
  // setSessionTemplate updates the global sessionTemplate (applies to ALL charts)
  const setSessionTemplateServer = useCallback(async (value) => {
    // Update global session template state
    setSessionTemplateState(value);

    // Persist to server in background
    if (user?.token) {
      await updateAppSettings(user.token, { sessionTemplate: value });
    }
  }, [user?.token, updateAppSettings]);

  // Chart interval/unit setters - now update the active chart
  const setChartIntervalAndUnitWithSync = useCallback(async (intervalValue, unitValue) => {
    if (!activeChartId) return;
    await updateChartSettings(activeChartId, { 
      interval: intervalValue, 
      unit: unitValue 
    });
  }, [activeChartId, updateChartSettings]);

  const setChartIntervalWithSync = useCallback(async (value) => {
    console.warn('[deprecated] setChartIntervalWithSync: prefer setChartIntervalAndUnitWithSync');
    return setChartIntervalAndUnitWithSync(value, chartUnit);
  }, [setChartIntervalAndUnitWithSync, chartUnit]);

  const setChartUnitWithSync = useCallback(async (value) => {
    console.warn('[deprecated] setChartUnitWithSync: prefer setChartIntervalAndUnitWithSync');
    return setChartIntervalAndUnitWithSync(chartInterval, value);
  }, [setChartIntervalAndUnitWithSync, chartInterval]);

  /**
   * Centralized handler for active chart changes (used for targeting)
   * Handles all side effects when user explicitly changes active chart:
   * - Resets limit/stop if ticker changes and targeting is enabled (triggers auto-calculation)
   * - Persists to server
   * NOTE: This is called when clicking target button or toggling targeting, NOT when updating chart settings
   */
  const setTargetedChartWithSync = useCallback(async (chartId) => {
    // Validate chart exists
    if (chartId && !charts.find(c => c.id === chartId)) {
      console.warn(`[setTargetedChart] Chart ${chartId} not found`);
      return;
    }
    
    // If chartId is null, this is a request to disable targeting (toggle off)
    // In the new system, we don't have an "untargeted" state - we always have an active chart
    // So null means "toggle targeting mode off"
    if (chartId === null) {
      setTargetingModeEnabled(prev => !prev);
      return;
    }
    
    // CRITICAL FIX: Set flag to prevent useEffects from disabling targeting mode during chart switch
    isSwitchingChartsRef.current = true;
    
    // Get the old and new tickers
    const oldChart = charts.find(c => c.id === activeChartId);
    const newChart = charts.find(c => c.id === chartId);
    const oldTicker = oldChart?.ticker;
    const newTicker = newChart?.ticker;
    
    // CRITICAL FIX: Update activeChartId FIRST before resetting prices
    // This ensures the selectedTicker updates immediately, preventing any useEffects
    // that depend on selectedTicker from running with stale data
    setActiveChartId(chartId);
    
    // Reset bracket prices ONLY when switching between two DIFFERENT defined tickers
    // If tickers are the same, keep existing prices to preserve bracket configuration
    if (oldTicker && newTicker && oldTicker !== newTicker) {
      // Always reset limit/stop loss (useful even when targeting disabled)
      setLimitPrice(0);
      setStopLoss(0);
      setStopLossTouched(false);
      
      // Only reset bracket-specific prices if targeting mode is enabled
      if (targetingModeEnabled) {
        setTakeProfitPrices([]);
        setTpInitialized(false);
        setTakeProfitTouched(false);
        // Note: Take profits will be recalculated by Trade.js auto-calc useEffect
      }
    }
    // If tickers are the same, DON'T reset prices - keep the existing bracket configuration
    // This allows users to switch between charts with the same ticker without losing their bracket setup

    // Persist to server in background
    if (user?.token) {
      await updateAppSettings(user.token, {
        charts,
        activeChartId: chartId,
        chartLayout,
        savedChartLayouts: savedChartLayoutsRef.current,
      });
    }
    
    // CRITICAL FIX: Clear the flag after a short delay to allow React to process all state updates
    // This prevents useEffects from disabling targeting mode during the chart switch
    setTimeout(() => {
      isSwitchingChartsRef.current = false;
    }, 100);
  }, [charts, activeChartId, targetingModeEnabled, chartLayout, user?.token, updateAppSettings, setLimitPrice, setStopLoss, setTakeProfitPrices, setTpInitialized, setTargetingModeEnabled]);

  /**
   * Set active chart with server persistence (called when clicking on charts)
   * When targeting mode is enabled, also updates which chart shows brackets
   * Uses startTransition to mark this as non-urgent to prevent blocking UI
   */
  const setActiveChartWithSync = useCallback(async (chartId) => {
    if (!chartId || chartId === activeChartId) {
      return; // Skip if already active or invalid
    }
    
    // If targeting mode is enabled, also update which chart shows brackets
    // This ensures brackets move to the newly active chart
    if (targetingModeEnabled) {
      // Call setTargetedChart to move brackets to the new active chart
      // This will handle ticker changes and bracket price resets if needed
      await setTargetedChartWithSync(chartId);
      return; // setTargetedChartWithSync already updates activeChartId and persists
    }
    
    // If targeting mode is disabled, just update activeChartId for focus tracking
    // Use startTransition to mark state update as non-urgent
    // This prevents blocking the UI during chart switching
    if (typeof startTransition !== 'undefined') {
      startTransition(() => {
        setActiveChartId(chartId);
      });
    } else {
      setActiveChartId(chartId);
    }

    // Persist to server in background (non-blocking)
    if (user?.token) {
      updateAppSettings(user.token, {
        charts,
        activeChartId: chartId,
        chartLayout,
        savedChartLayouts: savedChartLayoutsRef.current,
      }).catch(err => {
        console.error('Failed to persist active chart:', err);
      });
    }
  }, [activeChartId, charts, chartLayout, targetingModeEnabled, user?.token, updateAppSettings, setTargetedChartWithSync]);

  // Add a new chart
  const addChart = useCallback(async (chartConfig = {}) => {
    // Get the active chart's ticker to use as default for new chart
    const activeChart = charts.find(c => c.id === activeChartId);
    const defaultTicker = activeChart?.ticker || null;
    
    const newChart = {
      id: `chart${Date.now()}`,
      ticker: defaultTicker, // Use active chart's ticker as default
      interval: 5,
      unit: 'Minute',
      barsback: 200,
      indicators: [],
      ...chartConfig
    };
    
    const updatedCharts = [...charts, newChart];
    setCharts(updatedCharts);

    if (!user?.token) return;

    await updateAppSettings(user.token, {
      charts: updatedCharts,
      activeChartId,
      chartLayout,
      savedChartLayouts: savedChartLayoutsRef.current,
    });
    
    return newChart.id;
  }, [charts, activeChartId, chartLayout, user?.token, updateAppSettings]);

  // Remove a chart
  const removeChart = useCallback(async (chartId) => {
    if (charts.length <= 1) {
      console.warn('Cannot remove last chart');
      return;
    }

    const updatedCharts = charts.filter(c => c.id !== chartId);
    setCharts(updatedCharts);

    // If removing active chart, set first chart as active
    let newActiveId = activeChartId;
    if (chartId === activeChartId) {
      newActiveId = updatedCharts[0].id;
      setActiveChartId(newActiveId);
    }

    if (!user?.token) return;

    await updateAppSettings(user.token, {
      charts: updatedCharts,
      activeChartId: newActiveId,
      chartLayout,
      savedChartLayouts: savedChartLayoutsRef.current,
    });
  }, [charts, activeChartId, chartLayout, user?.token, updateAppSettings]);

  // Set chart layout and persist
  const setChartLayoutWithSync = useCallback(async (layout) => {
    const visibleCount = getRequiredChartCount(layout) || 1;
    const visibleCharts = charts.slice(0, visibleCount);
    const visibleChartIds = visibleCharts.map(c => c.id);
    
    // If the current activeChartId is not visible in the new layout, set it to the first visible chart
    let newActiveChartId = activeChartId;
    if (!visibleChartIds.includes(activeChartId) && visibleCharts.length > 0 && visibleCharts[0]?.id) {
      newActiveChartId = visibleCharts[0].id;
      setActiveChartId(newActiveChartId);
      console.log(`[AppStateContext] Layout changed to ${layout}, updating activeChartId from ${activeChartId} to ${newActiveChartId}`);
    }
    
    setChartLayout(layout);

    if (!user?.token) return;

    await updateAppSettings(user.token, {
      charts,
      activeChartId: newActiveChartId,
      chartLayout: layout,
      savedChartLayouts: savedChartLayoutsRef.current,
    });
  }, [charts, activeChartId, user?.token, updateAppSettings]);

  const persistWorkspaceSettings = useCallback(async (patch) => {
    if (!user?.token) return;
    if (Array.isArray(patch?.savedChartLayouts)) {
      savedChartLayoutsRef.current = patch.savedChartLayouts;
    }
    try {
      await updateAppSettings(user.token, {
        charts,
        activeChartId,
        chartLayout,
        savedChartLayouts: savedChartLayoutsRef.current,
        ...patch,
      });
    } catch (err) {
      console.error('Failed to persist chart layouts:', err);
    }
  }, [charts, activeChartId, chartLayout, user?.token, updateAppSettings]);

  const saveChartLayout = useCallback(async (name) => {
    const trimmed = String(name || '').trim().slice(0, 40);
    if (!trimmed) return null;
    const snapshot = snapshotVisibleCharts(charts, chartLayout);
    const entry = {
      id: `layout_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      name: trimmed,
      chartLayout: snapshot.chartLayout,
      charts: snapshot.charts,
    };
    const nextLayouts = [entry, ...savedChartLayouts];
    savedChartLayoutsRef.current = nextLayouts;
    setSavedChartLayouts(nextLayouts);
    await persistWorkspaceSettings({ savedChartLayouts: nextLayouts });
    return entry;
  }, [charts, chartLayout, savedChartLayouts, persistWorkspaceSettings]);

  const deleteChartLayout = useCallback(async (layoutId) => {
    const nextLayouts = savedChartLayouts.filter((layout) => layout.id !== layoutId);
    savedChartLayoutsRef.current = nextLayouts;
    setSavedChartLayouts(nextLayouts);
    await persistWorkspaceSettings({ savedChartLayouts: nextLayouts });
  }, [savedChartLayouts, persistWorkspaceSettings]);

  const applySavedChartLayout = useCallback(async (layoutId) => {
    const saved = savedChartLayouts.find((layout) => layout.id === layoutId);
    if (!saved) return;
    const applied = applySavedLayoutToCharts(charts, saved);
    if (!applied) return;

    let newActiveId = activeChartId;
    if (!applied.visibleIds.includes(activeChartId) && applied.visibleIds[0]) {
      newActiveId = applied.visibleIds[0];
    }

    const previousTicker = charts.find((chart) => chart.id === activeChartId)?.ticker || null;
    const nextTicker = applied.charts.find((chart) => chart.id === newActiveId)?.ticker || null;
    if (previousTicker !== nextTicker) {
      setLimitPrice(0);
      setStopLoss(0);
      setStopLossTouched(false);
      if (targetingModeEnabled) {
        setTakeProfitPrices([]);
        setTpInitialized(false);
        setTakeProfitTouched(false);
      }
    }

    const patch = {
      charts: applied.charts,
      chartLayout: applied.chartLayout,
      activeChartId: newActiveId,
    };
    if (layoutHasMixedTickers(saved.charts) && syncByTicker) {
      localValuesRef.current.syncByTicker = false;
      setSyncByTickerState(false);
      patch.syncByTicker = false;
    }
    if (layoutHasMixedTimeframes(saved.charts) && syncByTimeframe) {
      localValuesRef.current.syncByTimeframe = false;
      setSyncByTimeframeState(false);
      patch.syncByTimeframe = false;
    }

    setCharts(applied.charts);
    setChartLayout(applied.chartLayout);
    if (newActiveId !== activeChartId) setActiveChartId(newActiveId);
    await persistWorkspaceSettings(patch);
  }, [
    savedChartLayouts,
    charts,
    activeChartId,
    targetingModeEnabled,
    syncByTicker,
    syncByTimeframe,
    persistWorkspaceSettings,
    setLimitPrice,
    setStopLoss,
    setTakeProfitPrices,
    setTpInitialized,
  ]);

  // Memoize context value to prevent unnecessary re-renders
  return useMemo(() => ({
    riskPercentage,
    setRiskPercentage: setAccountSpecificRiskPercentage,
    risk,
    setRisk: setAccountSpecificRisk,
    setRiskAndPercentage, // Batched setter to avoid race conditions
    riskPresets,
    setRiskPresets: setRiskPresetsServer,
    addRiskPreset,
    removeRiskPreset,
    trailingStopLoss,
    setTrailingStopLoss,
    stopLoss,
    setStopLoss: setStopLossValidated,
    limitPrice,
    setLimitPrice: setLimitPriceValidated,
    quantity,
    setQuantity: setQuantityValidated,
    positionSizeMode,
    setPositionSizeMode,
    spendAmount,
    setSpendAmount,
    oneRSplit,
    setOneRSplit,
    manualQuantityOverride,
    setManualQuantityOverride: setManualQuantityOverrideServer,
    limitSyncEnabled,
    setLimitSyncEnabled,
    bracketRLevel,
    setBracketRLevel,
    takeProfitPercentage,
    setTakeProfitPercentage,
    liquidityLevelsCount,
    setLiquidityLevelsCount: setLiquidityLevelsCountServer,
    showStdDevLines,
    setShowStdDevLines: setShowStdDevLinesServer,
    showOrders,
    setShowOrders: setShowOrdersServer,
    showTradeExecutions,
    setShowTradeExecutions: setShowTradeExecutionsServer,
    showRuleViolations,
    setShowRuleViolations: setShowRuleViolationsServer,
    showAlertsOnChart,
    setShowAlertsOnChart: setShowAlertsOnChartServer,
    showCrosshair,
    setShowCrosshair: setShowCrosshairServer,
    lockOrderDragging,
    setLockOrderDragging: setLockOrderDraggingServer,
    logScale,
    setLogScale: setLogScaleServer,
    extendPriceLinesFull,
    setExtendPriceLinesFull: setExtendPriceLinesFullServer,
    chartBackground,
    setChartBackground,
    candlestickUpColor,
    setCandlestickUpColor,
    candlestickDownColor,
    setCandlestickDownColor,
    candlestickFillUp,
    setCandlestickFillUp,
    candlestickFillDown,
    setCandlestickFillDown,
    strategyEnabled,
    setStrategyEnabled,
    bracketType,
    setBracketType: setBracketTypeServer,
    manualAtr,
    setManualAtr: setManualAtrServer,
    timeInForce,
    setTimeInForce: setTimeInForceServer,
    orderType,
    setOrderType: setOrderTypeServer,
    // Expose raw local setter (non-persisting) if ever needed
    setBracketTypeLocal: setBracketType,
    // Bracket Templates
    bracketTemplates,
    selectedBracketTemplateId,
    setSelectedBracketTemplateId: setSelectedBracketTemplateIdServer,
    setSelectedBracketTemplateIdLocal: setSelectedBracketTemplateId, // Local non-persisting version
    getSelectedBracketTemplate,
    createBracketTemplate,
    updateBracketTemplate,
    deleteBracketTemplate,
    orderBracketOverrides,
    setOrderBracketOverrides,
    // Unified bracket mode
    bracketMode,
    setBracketMode: setBracketModeServer,
    // Legacy modes (derived from bracketMode)
    bracketAllocationMode,
    setBracketAllocationMode: setBracketAllocationModeServer,
    bracketPriceMode,
    setBracketPriceMode: setBracketPriceModeServer,
    sessionTemplate,
    setSessionTemplate: setSessionTemplateServer,
    takeProfitPrices,
    setTakeProfitPrices,
    entryTakeProfits,
    setEntryTakeProfits,
    takeProfitTouched,
    setTakeProfitTouched,
    stopLossTouched,
    setStopLossTouched,
    bracketDraftMode,
    setBracketDraftMode,
    setTpInitialized, // Expose to allow resetting TP auto-calc
    lastBracketSide, // Track last bracket side preference (long/short)
    // Chart settings
    chartInterval,
    setChartInterval: setChartIntervalWithSync,
    chartUnit,
    setChartUnit: setChartUnitWithSync,
    setChartIntervalAndUnit: setChartIntervalAndUnitWithSync,
    mobileWatchlistsCollapsed,
    setMobileWatchlistsCollapsed,
    mobileChartCollapsed,
    setMobileChartCollapsed,
    mobileOrderSettingsCollapsed,
    setMobileOrderSettingsCollapsed,
    settingsLoaded,
    getAccountRiskSettings,
    // Trade Alerts
    tradeAlerts,
    tradeAlertsLoading,
    tradeAlertsError,
    fetchTradeAlerts,
    draftAlert,
    setDraftAlert,
    // Technical Indicators
    indicators,
    indicatorsLoading,
    indicatorsError,
    fetchIndicators,
    fetchSpecificIndicator,
    // Watchlists
    watchlists,
    selectedWatchlistId,
    setSelectedWatchlistId: setSelectedWatchlistIdServer,
    watchlistTickers,
    watchlistTickersLoading,
    watchlistsLoading,
    watchlistsError,
    loadWatchlists,
    loadWatchlistTickers,
    createWatchlist,
    renameWatchlist,
    deleteWatchlist,
    addWatchlistTicker,
    removeWatchlistTicker,
    reorderWatchlistTickers,
    // Multi-Chart (NEW)
    charts,
    activeChartId,
    chartLayout,
    savedChartLayouts,
    saveChartLayout,
    deleteChartLayout,
    applySavedChartLayout,
    targetingModeEnabled,
    setTargetingModeEnabled,
    // Position bracket mode for adding SL/TP to EXISTING positions (separate from targeting mode)
    positionBracketMode: positionBracketModeEnriched,
    startPositionBracketMode,
    setPositionBracketSL,
    setPositionBracketTPs,
    setPositionBracketTakeProfits,
    addPositionBracketTP,
    endPositionBracketMode,
    selectedTicker, // Derived from active chart's ticker when targeting mode enabled (for order entry)
    activeChartTicker, // Derived from active chart's ticker (for position management)
    tradeWorkspaceView,
    setTradeWorkspaceView,
    optionsChainState,
    setOptionsChainState,
    setExpirationExpanded,
    getChart,
    getTargetedChart,
    updateChartSettings,
    updateChartsInBatch,
    setTargetedChart: setTargetedChartWithSync,
    setActiveChart: setActiveChartWithSync, // Set which chart is currently focused/clicked
    setChartLayout: setChartLayoutWithSync,
    addChart,
    removeChart,
    // Chart Synchronization
    syncByTicker,
    setSyncByTicker,
    syncByTimeframe,
    setSyncByTimeframe,
    syncCrosshair,
    setSyncCrosshair,
  }), [
    // State values
    riskPercentage,
    risk,
    riskPresets,
    trailingStopLoss,
    stopLoss,
    limitPrice,
    quantity,
    positionSizeMode,
    spendAmount,
    oneRSplit,
    manualQuantityOverride,
    limitSyncEnabled,
    bracketRLevel,
    takeProfitPercentage,
    liquidityLevelsCount,
    showStdDevLines,
    showOrders,
    showTradeExecutions,
    showRuleViolations,
    showAlertsOnChart,
    showCrosshair,
    lockOrderDragging,
    logScale,
    extendPriceLinesFull,
    strategyEnabled,
    bracketType,
    manualAtr,
    timeInForce,
    orderType,
    bracketTemplates,
    selectedBracketTemplateId,
    orderBracketOverrides,
    bracketMode,
    bracketAllocationMode,
    bracketPriceMode,
    sessionTemplate,
    takeProfitPrices,
    entryTakeProfits,
    takeProfitTouched,
    stopLossTouched,
    bracketDraftMode,
    lastBracketSide,
    mobileWatchlistsCollapsed,
    mobileChartCollapsed,
    mobileOrderSettingsCollapsed,
    settingsLoaded,
    tradeAlerts,
    tradeAlertsLoading,
    tradeAlertsError,
    draftAlert,
    indicators,
    indicatorsLoading,
    indicatorsError,
    watchlists,
    selectedWatchlistId,
    watchlistTickers,
    watchlistTickersLoading,
    watchlistsLoading,
    watchlistsError,
    charts,
    activeChartId,
    chartLayout,
    savedChartLayouts,
    targetingModeEnabled,
    positionBracketModeEnriched,
    entryTakeProfits,
    syncByTicker,
    syncByTimeframe,
    syncCrosshair,
    // Derived values
    selectedTicker,
    activeChartTicker,
    chartInterval,
    chartUnit,
    // Stable functions (useCallback/useMemo) - included for completeness but won't change
    setAccountSpecificRiskPercentage,
    setAccountSpecificRisk,
    setRiskAndPercentage,
    setRiskPresetsServer,
    addRiskPreset,
    removeRiskPreset,
    setTrailingStopLoss,
    setStopLossValidated,
    setLimitPriceValidated,
    setQuantityValidated,
    setManualQuantityOverrideServer,
    setLimitSyncEnabled,
    setBracketRLevel,
    setTakeProfitPercentage,
    setLiquidityLevelsCountServer,
    setShowStdDevLinesServer,
    setShowOrdersServer,
    setShowTradeExecutionsServer,
    setShowRuleViolationsServer,
    setShowAlertsOnChartServer,
    setShowCrosshairServer,
    setLockOrderDraggingServer,
    setLogScaleServer,
    setExtendPriceLinesFullServer,
    setStrategyEnabled,
    setBracketTypeServer,
    setManualAtrServer,
    setTimeInForceServer,
    setOrderTypeServer,
    setBracketType,
    setSelectedBracketTemplateIdServer,
    setSelectedBracketTemplateId,
    getSelectedBracketTemplate,
    createBracketTemplate,
    updateBracketTemplate,
    deleteBracketTemplate,
    setOrderBracketOverrides,
    setBracketModeServer,
    setBracketAllocationModeServer,
    setBracketPriceModeServer,
    setSessionTemplateServer,
    setTakeProfitPrices,
    setTakeProfitTouched,
    setStopLossTouched,
    setTpInitialized,
    setEntryTakeProfits,
    setChartIntervalWithSync,
    setChartUnitWithSync,
    setChartIntervalAndUnitWithSync,
    getAccountRiskSettings,
    fetchTradeAlerts,
    setDraftAlert,
    fetchIndicators,
    fetchSpecificIndicator,
    setSelectedWatchlistIdServer,
    loadWatchlists,
    loadWatchlistTickers,
    createWatchlist,
    renameWatchlist,
    deleteWatchlist,
    addWatchlistTicker,
    removeWatchlistTicker,
    reorderWatchlistTickers,
    setTargetingModeEnabled,
    // Position bracket mode functions
    startPositionBracketMode,
    setPositionBracketSL,
    setPositionBracketTPs,
    addPositionBracketTP,
    endPositionBracketMode,
    getChart,
    getTargetedChart,
    updateChartSettings,
    updateChartsInBatch,
    setTargetedChartWithSync,
    setActiveChartWithSync,
    setChartLayoutWithSync,
    saveChartLayout,
    deleteChartLayout,
    applySavedChartLayout,
    addChart,
    removeChart,
    setSyncByTicker,
    setSyncByTimeframe,
    setSyncCrosshair,
    tradeWorkspaceView,
    optionsChainState,
    setTradeWorkspaceView,
    setOptionsChainState,
    setExpirationExpanded,
  ]);
}

export const AppStateContext = createContext();

export const AppStateContextProvider = ({ children }) => (
  <AppStateContext.Provider value={useAppStateContext()}>
    {children}
  </AppStateContext.Provider>
);